import { shipmentMethodsMap, LOYALTY_CARD_LABELS } from 'constants/index';
import { getRetailPrice, formatNumberToUSD } from 'utils';
import actionTypes from '../actions/actionTypes';

const INITIAL_STATE = {
  promo: {
    keys: [],
    codes: [],
    lastApplied: [],
    error: '',
  },
  loading: false,
  error: false,
  shipmentMethods: [],
  gcPayments: [],
  orderId: '',
  bopsStores: [],
  bopsAddress: null,
  storePickupMethod: {},
};

/**
 * For BG Brand, if the sumtotal of all regular items in Checkout is more than $700, gift wrap is Free,
 * excluding sale and adjusted items from the sumtotal. (Business asked to override
 * the gift wrapping fee and total in checkout, order receipt and order confirmation page)
 * For more details see: https://neimanmarcus.atlassian.net/browse/OSP-9297
 *
 */

export const setOrderTotalWithFreeGiftWrap = ({
  shipmentMethods = INITIAL_STATE.shipmentMethods,
  newGiftWrapCharge,
  giftWrappingCharges,
  isBgElevatedPackagingEligible,
}) => {
  return shipmentMethods.map((method) => {
    const giftWrapChargeCalc = isBgElevatedPackagingEligible
      ? newGiftWrapCharge || method.giftWrapCharge
      : method.giftWrapCharge;

    const giftWrappingChargesCalc = isBgElevatedPackagingEligible
      ? giftWrappingCharges || method.giftWrappingCharges
      : method.giftWrappingCharges;

    return {
      ...method,
      giftWrapCharge: giftWrapChargeCalc,
      giftWrappingCharges: giftWrappingChargesCalc,
      isBgElevatedPackagingEligible,
    };
  });
};

export const mapShipmentData = ({ shipmentOptions, items }) => {
  return shipmentOptions
    .map((option) => {
      const shipmentMethod = shipmentMethodsMap.find((method) => method.id === option.shipmentOption) || {};

      // We need to find first `giftWrapCharge` in items, which is greater then 0
      // Specially for https://neimanmarcus.atlassian.net/browse/OSP-723
      const itemWithGiftWrapCharge =
        option.items.find(({ giftWrapCharge }) => !!parseInt(giftWrapCharge, 10) && giftWrapCharge) || {};

      const promotionTotalAmount = option.items
        ?.reduce((acc, item) => acc + +(item.promotionDiscAmt || 0), 0)
        .toFixed(2);

      const priceTotalAmount = option.items
        ?.reduce((acc, item) => acc + +(item.itemPriceEach * item.quantity || 0), 0)
        .toFixed(2);

      const unavailableItems = items
        .filter((item) => !option.items.find((i) => i.cmosSku === item.cmosskuId))
        .map((item) => item.itemskuId)
        .join(',');

      const retailPrice = getRetailPrice(priceTotalAmount, promotionTotalAmount).toFixed(2);

      const lineDeliveryCharges = option.items?.reduce((acc, item) => [...acc, item.lineDeliveryCharge], []);
      const itemTaxes = option.items?.reduce((acc, item) => [...acc, item.itemTax], []) || [];
      const giftWrappingCharges =
        option.items?.reduce((acc, item) => [...acc, item.giftWrapCharge || '0.00'], []) || [];

      return {
        ...shipmentMethod,
        unavailableItems,
        subTotalPrice: formatNumberToUSD(Number(retailPrice)) || `$${retailPrice}`,
        orderTotalAmt: `$${option.orderTotalAmt}`,
        orderTotalTax: formatNumberToUSD(Number(option.orderTotalTax)) || `$${option.orderTotalTax}`,
        deliveryCharges: `$${option.deliveryCharges}`,
        giftPackaging: `$${option.totalGiftWrapCharge}`,
        promotion: `-$${promotionTotalAmount}`,
        giftCard: '-$0',
        giftWrapCharge: itemWithGiftWrapCharge.giftWrapCharge,
        lineDeliveryCharges,
        itemTaxes,
        giftWrappingCharges,
        items: option.items,
      };
    })
    .sort((a, b) => (a.id > b.id ? -1 : 1));
};

export const updateShipmentsWithGiftCards = (shipmentOptions, giftCards) =>
  shipmentOptions.map((option) => {
    const appliedGiftCards = giftCards.filter((p) => p.selected);
    const totalCardDiscount = +appliedGiftCards.reduce((total, payment) => total + +(payment.giftCardBalance || 0), 0);

    const totalAmt = +option.orderTotalAmt.slice(1);
    const giftCardDiscount = totalAmt > totalCardDiscount ? totalCardDiscount : totalAmt;
    const orderTotalWithGiftCard = totalAmt - giftCardDiscount;

    return {
      ...option,
      orderTotalWithGiftCard: `$${orderTotalWithGiftCard.toFixed(2)}`,
      giftCard: `-$${giftCardDiscount.toFixed(2)}`,
    };
  });

export const mapAppliedPromos = (promo, shipmentOptions) => {
  const { lastApplied } = promo;

  const allPromos = shipmentOptions.reduce(
    (promo, method) => {
      return method.items.reduce((acc, item) => {
        return {
          keys: [...acc.keys, ...(item.promotionKeyWords || [])],
          codes: [...acc.codes, ...(item.promotionsApplied || [])],
        };
      }, promo);
    },
    {
      keys: [],
      codes: [],
    },
  );

  const failedPromoCodes = lastApplied.filter((code) => !allPromos.keys.includes(code));

  return {
    ...promo,
    error: failedPromoCodes.join(', '),
    lastApplied: [],
    keys: [...new Set(allPromos.keys)],
    codes: [...new Set(allPromos.codes)],
  };
};

export const mapLoyaltyCards = (loyaltyAssets, gcPayments) => {
  const giftCardNumbers = gcPayments.map(({ giftCardNumber }) => giftCardNumber);
  const loyaltyCards = []
    .concat(...Object.values(loyaltyAssets))
    .filter(({ cardNumber, currentBal }) => !giftCardNumbers.includes(cardNumber.toString()) && Number(currentBal) > 0)
    .filter(({ cardType }) => cardType !== 'InCircle Perk');
  const lCards = loyaltyCards.map((card) => ({
    giftCardNumber: card.cardNumber.toString(),
    giftCardPin: '0000',
    cardType: 'NEX',
    giftCardBalance: card.currentBal,
    giftCardLabel: LOYALTY_CARD_LABELS[card.cardType],
    selected: false,
    isLoyalty: true,
    expDate: card.expDate,
  }));

  return [...gcPayments, ...lCards];
};

const updateLoyaltyCardBalance = (loyaltyCard, gcPayments) => {
  return gcPayments.map((p) => {
    if (p.giftCardNumber.toString() === loyaltyCard.giftCardNumber.toString()) {
      return {
        ...p,
        giftCardBalance: loyaltyCard.giftCardBalance,
      };
    }
    return p;
  });
};

const checkout = (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    // Need to reset Gift Card Payments whenever client changes
    case actionTypes.CLIENT_DETAILS_SUCCESS:
    case actionTypes.CLIENTS_CENTRAL_SEARCH_LOADING:
      return { ...state, gcPayments: [] };

    case actionTypes.ORDER_REVIEW_LOADING:
      return {
        ...state,
        loading: true,
      };

    case actionTypes.ORDER_REVIEW_ERROR:
      return {
        ...state,
        shipmentMethods: [],
        loading: false,
        error: true,
      };

    case actionTypes.ORDER_REVIEW_SUCCESS:
      return {
        ...state,
        shipmentMethods: updateShipmentsWithGiftCards(mapShipmentData(payload), state.gcPayments),
        promo: mapAppliedPromos(state.promo, payload.shipmentOptions),
        loading: false,
        error: false,
      };

    case actionTypes.ORDER_REVIEW_STORE_PICKUP_SUCCESS:
      return {
        ...state,
        storePickupMethod: updateShipmentsWithGiftCards(mapShipmentData(payload), state.gcPayments)[0],
        promo: mapAppliedPromos(state.promo, payload.shipmentOptions),
        loading: false,
        error: false,
      };

    case actionTypes.APPLY_PROMO:
      return {
        ...state,
        promo: {
          ...state.promo,
          lastApplied: payload,
        },
      };

    case actionTypes.REMOVE_PROMO:
      return {
        ...state,
        promo: {
          ...state.promo,
          keys: state.promo.keys.filter((key) => key !== payload),
        },
      };

    case actionTypes.SUBMIT_ORDER_LOADING:
      return {
        ...state,
        loading: true,
      };

    case actionTypes.SUBMIT_ORDER_ERROR:
      return {
        ...state,
        loading: false,
        error: true,
      };

    case actionTypes.SUBMIT_ORDER_SUCCESS:
      return {
        ...state,
        orderId: payload,
        loading: false,
        error: false,
        promo: { ...INITIAL_STATE.promo },
      };

    case actionTypes.REMOVE_ORDER_ID:
      return {
        ...state,
        orderId: '',
      };

    case actionTypes.RESET_PROMO:
      return {
        ...state,
        promo: { ...INITIAL_STATE.promo },
      };

    case actionTypes.ORDER_REVIEW_CLEAR:
      return {
        ...state,
        shipmentMethods: INITIAL_STATE.shipmentMethods,
      };

    case actionTypes.ADD_GIFT_CARD_PAYMENT_SUCCESS: {
      // `isNew` property indicates that gc option has been added in via `AddGiftCardForm`
      // but changes have not been confirmed on `CheckoutPaymentForm` yet. Specially for OSP-5394
      const gcPayments = [...state.gcPayments, { cardType: 'NEX', isNew: true, selected: true, ...payload }];
      return {
        ...state,
        gcPayments,
      };
    }

    case actionTypes.ADD_LOYALTY_CARD_PAYMENTS: {
      const gcPayments = mapLoyaltyCards(payload, state.gcPayments);
      return {
        ...state,
        gcPayments,
        shipmentMethods: updateShipmentsWithGiftCards(state.shipmentMethods, gcPayments),
      };
    }

    case actionTypes.UPDATE_LOYALTY_CARD_BALANCE: {
      const gcPayments = updateLoyaltyCardBalance(payload, state.gcPayments);
      return {
        ...state,
        gcPayments,
      };
    }

    case actionTypes.UPDATE_GIFT_CARD_PAYMENTS: {
      const gcPayments = [...payload];
      return {
        ...state,
        gcPayments,
        shipmentMethods: updateShipmentsWithGiftCards(state.shipmentMethods, gcPayments),
      };
    }

    case actionTypes.STORE_INFORMATION_LOADING:
      return {
        ...state,
        loading: true,
      };

    case actionTypes.STORE_INFORMATION_SUCCESS: {
      return {
        ...state,
        loading: false,
        bopsAddress: payload,
      };
    }

    case actionTypes.STORE_INFORMATION_ERROR:
      return {
        ...state,
        loading: false,
        error: true,
      };

    case actionTypes.SEARCH_STORE_LOADING:
      return {
        ...state,
        loading: true,
      };

    case actionTypes.SEARCH_STORE_SUCCESS: {
      return {
        ...state,
        bopsStores: payload,
        loading: false,
      };
    }

    case actionTypes.SEARCH_STORE_ERROR:
      return {
        ...state,
        loading: false,
        error: true,
      };

    case actionTypes.RESET_STORE_INFORMATION: {
      return {
        ...state,
        bopsAddress: '',
      };
    }

    case actionTypes.SET_STORE_PICKUP_METHOD: {
      return {
        ...state,
        storePickupMethod: payload,
      };
    }

    case actionTypes.SET_BG_ELEVATED_GIFT_PACKAGING_METHOD:
      return {
        ...state,
        shipmentMethods: setOrderTotalWithFreeGiftWrap(payload),
      };

    default:
      return state;
  }
};

export default checkout;
